/** Frontend wrapper for /workspace-bots. One method per route, getAuthHeaders
 * on every call, errors thrown with the server's message. */
import { API_BASE, getAuthHeaders } from '../utils/supabase/client';
import { throwApiError } from './BotApiError';
import { rememberBotAppearances, type BotAccentColor, type BotAvatarStyle } from '../features/workspaces/bots/botAppearance';

async function readError(res: Response): Promise<string> {
  try {
    const d = await res.json();
    return (d && (d.error || d.message)) || `HTTP ${res.status}`;
  } catch {
    return `HTTP ${res.status}`;
  }
}

export type BotCapability =
  | 'read_projects' | 'read_tasks' | 'read_documents' | 'read_calendar' | 'create_tasks' | 'message_members';

export interface WorkspaceBot {
  id: string;
  userId: string | null;
  workspaceId: string;
  name: string;
  instructions: string;
  capabilities: BotCapability[];
  isEnabled: boolean;
  createdBy: string | null;
  createdAt: string;
  updatedAt: string;
  /** Null until an admin picks one; see resolveBotAppearance. */
  avatarStyle: BotAvatarStyle | null;
  avatarColor: BotAccentColor | null;
  /** Older servers omit it; treat missing as false. */
  taskParallel?: boolean;
}

export interface WorkspaceBotRun {
  id: string;
  status: 'queued' | 'processing' | 'completed' | 'failed';
  chatId: string | null;
  /** Older servers omit it; treat a missing value as a chat reply. */
  source?: 'chat' | 'quantis_ai' | 'task';
  toolsUsed: string[];
  turns: number;
  tokensIn: number | null;
  tokensOut: number | null;
  error: string | null;
  createdAt: string;
  completedAt: string | null;
}

export interface WorkspaceBotInput {
  name: string;
  instructions: string;
  capabilities: BotCapability[];
  avatarStyle?: BotAvatarStyle | null;
  avatarColor?: BotAccentColor | null;
  taskParallel?: boolean;
}

const BASE = `${API_BASE}/workspace-bots`;

function remembered(bot: WorkspaceBot): WorkspaceBot {
  rememberBotAppearances([bot]);
  return bot;
}

export class WorkspaceBotsService {
  async list(workspaceId: string): Promise<WorkspaceBot[]> {
    const res = await fetch(`${BASE}?workspaceId=${encodeURIComponent(workspaceId)}`, { headers: await getAuthHeaders() });
    if (!res.ok) throw new Error(await readError(res));
    const data = await res.json();
    const bots: WorkspaceBot[] = Array.isArray(data?.bots) ? data.bots : [];
    rememberBotAppearances(bots);
    return bots;
  }

  /** `warning` is set when the agent was created but part of it, such as its
   * chosen look, could not be saved. */
  async create(input: WorkspaceBotInput & { workspaceId: string }): Promise<{ bot: WorkspaceBot; warning: string | null }> {
    const res = await fetch(BASE, {
      method: 'POST',
      headers: await getAuthHeaders({ 'Content-Type': 'application/json' }),
      body: JSON.stringify(input),
    });
    if (!res.ok) return throwApiError(res);
    const body = await res.json();
    return { bot: remembered(body.bot), warning: typeof body.warning === 'string' ? body.warning : null };
  }

  async update(id: string, patch: Partial<WorkspaceBotInput> & { isEnabled?: boolean }): Promise<WorkspaceBot> {
    const res = await fetch(`${BASE}/${encodeURIComponent(id)}`, {
      method: 'PATCH',
      headers: await getAuthHeaders({ 'Content-Type': 'application/json' }),
      body: JSON.stringify(patch),
    });
    if (!res.ok) return throwApiError(res);
    return remembered((await res.json()).bot);
  }

  async remove(id: string): Promise<void> {
    const res = await fetch(`${BASE}/${encodeURIComponent(id)}`, { method: 'DELETE', headers: await getAuthHeaders() });
    if (!res.ok) throw new Error(await readError(res));
  }

  async runs(id: string): Promise<WorkspaceBotRun[]> {
    const res = await fetch(`${BASE}/${encodeURIComponent(id)}/runs`, { headers: await getAuthHeaders() });
    if (!res.ok) throw new Error(await readError(res));
    const data = await res.json();
    return Array.isArray(data?.runs) ? data.runs : [];
  }
}

export const workspaceBotsService = new WorkspaceBotsService();
